import { uiCollator } from '@shared/lib'

/** Подпись города по словарю `cities` активного языка; нет перевода — имя из
 *  справочника бэка как есть. Только внутри `entities/user`: основа
 *  `useCityLabel` (подпись города вне списка) и `buildCityOptions` (выборы
 *  города); снаружи — через них, а не напрямую. */
export function getLocalizedCityName(
  bundle: Record<string, string> | undefined,
  backendName: string
): string {
  return bundle?.[backendName] ?? backendName
}

/** Имя строки «Другое» в справочнике бэка: всегда последняя в списке. */
export const CITY_OTHER_NAME = 'Other'

interface NamedCity {
  id: number
  name: string
}

/**
 * Город профиля — строка «Другое» справочника его страны. Строку ищем по имени,
 * а не по id: у стран он разный (KZ — 23, PH — 881). Профиль или справочник не
 * пришли (загрузка, ошибка) — `false`: лучше промолчать, чем позвать к выбору
 * города человека, у которого он уже из списка.
 */
export function isOtherCity(
  profile: { city: number | null } | null | undefined,
  cities: readonly NamedCity[] | null | undefined
): boolean {
  if (!profile || profile.city == null || !cities) return false
  return cities.some((c) => c.id === profile.city && c.name === CITY_OTHER_NAME)
}

/**
 * Опции выбора города: подпись по словарю языка интерфейса, порядок — по
 * подписи (бэк сортирует латинские имена, а подписи кириллицей идут в другом
 * порядке), «Другое» всегда в конце.
 */
export function buildCityOptions(
  cities: readonly NamedCity[] | undefined,
  bundle: Record<string, string> | undefined,
  language: string,
  getValue: (city: NamedCity) => string = (c) => String(c.id)
): { value: string; label: string }[] {
  const collator = uiCollator(language)
  return (cities ?? [])
    .map((c) => ({
      value: getValue(c),
      label: getLocalizedCityName(bundle, c.name),
      isOther: c.name === CITY_OTHER_NAME,
    }))
    .sort((a, b) => Number(a.isOther) - Number(b.isOther) || collator.compare(a.label, b.label))
    .map(({ value, label }) => ({ value, label }))
}

/**
 * Опции выбора города со значением-именем (то, что принимает бэк админской
 * карточки, а не id). Подписи и порядок — как в `buildCityOptions`. Имя, которого
 * нет в справочнике страны (устаревшее значение), остаётся выбираемой опцией
 * с подписью как есть: нетронутое поле отправит то же имя.
 */
export function buildCityNameOptions(
  cities: readonly NamedCity[] | undefined,
  bundle: Record<string, string> | undefined,
  language: string,
  current?: string
): { value: string; label: string }[] {
  const options = buildCityOptions(cities, bundle, language, (c) => c.name)
  if (!current || options.some((o) => o.value === current)) return options
  return [{ value: current, label: current }, ...options]
}
